import React, {useState} from 'react'
import {ObjUtil} from 'yougile-util'
import {IconTabler} from '@components/common/icon-tabler/icon-tabler'
import {noDefault} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {
  TwIconLeft,
  TwIconRight,
  TwLabel,
  TwSupport,
  TwTextArea,
  TwTextAreaHeight,
  TwWrap
} from './containers'
import {TextAreaProps} from './types'
import {useActions} from './useActions'

export const TextArea = (props: TextAreaProps) => {
  const {label, support} = props

  if (label && support) {
    return (
      <TwWrap className={props.className}>
        <Label {...props} />
        <TextAreaComponent {...props} />
        <Support {...props} />
      </TwWrap>
    )
  }
  if (label) {
    return (
      <TwWrap className={props.className}>
        <Label {...props} />
        <TextAreaComponent {...props} />
      </TwWrap>
    )
  }
  if (support) {
    return (
      <TwWrap className={props.className}>
        <TextAreaComponent {...props} />
        <Support {...props} />
      </TwWrap>
    )
  }
  return <TextAreaComponent {...props} />
}

const Label = (props: TextAreaProps) => {
  const {label, mark = false, disabled = false} = props
  return (
    <TwLabel $disabled={disabled}>
      {label}
      {mark && <span className="text-error">&nbsp;*</span>}
    </TwLabel>
  )
}

const Support = (props: TextAreaProps) => {
  const {support, disabled = false} = props
  return <TwSupport $disabled={disabled}>{support}</TwSupport>
}

const TextAreaComponent = (props: TextAreaProps) => {
  const {disabled = false, isValid, hasError = false, isCorrect} = props
  const {icon, iconPosition = 'left', borders = true} = props
  const {paddings = 'medium', className, textAreaClassName} = props
  const {color = 'primary', noBackground = false, textSize = 'medium'} = props

  const leftPosition = iconPosition === 'left' || iconPosition === 'both'
  const rightPosition = iconPosition === 'right' || iconPosition === 'both'
  const svgSize = 16
  const iconTablerViewBox = '0 0 24 24'
  const leftIconPosition = Boolean(icon && leftPosition)
  const rightIconPosition = Boolean(icon && rightPosition)

  const [value, setValue] = useState('')
  const resetValue = (value = '') => {
    setValue(value)
  }
  const {validationType, ref, ...actions} = useActions({
    ...props,
    paddings,
    resetValue
  })

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const val = e?.currentTarget?.value || ' '
    const isNewLine = props.allowSoftNewLine && val[val.length - 1] === '\n'

    setValue(isNewLine ? val + ' ' : val)
    actions.onChange(e)
  }

  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>): void => {
    const currentTarget = e?.currentTarget
    // 2 fixes
    // 1 onPaste event throws early than target.value will change
    // 2 onChange clear value and props.paste overwrites real value by empty value
    setTimeout(() => {
      const val = currentTarget?.value || ''
      if (val) {
        setValue(val)
        actions.onPaste(val)
      }
    }, 200)
  }

  const inputStyle: React.CSSProperties = borders
    ? {...props.style, overflowY: 'auto'}
    : {...props.style, boxShadow: 'none'}

  return (
    <div className={cn('relative flex overflow-hidden', className)}>
      {leftIconPosition && icon && (
        <TwIconLeft $paddings={paddings} $color={color}>
          <IconTabler
            customSize={svgSize}
            icon={icon}
            viewBox={iconTablerViewBox}
          />
        </TwIconLeft>
      )}
      {rightIconPosition && icon && (
        <TwIconRight $paddings={paddings} $color={color}>
          <IconTabler
            customSize={svgSize}
            icon={icon}
            viewBox={iconTablerViewBox}
          />
        </TwIconRight>
      )}
      <TwTextArea
        {...ObjUtil.pick(
          props,
          'autoComplete',
          'autoFocus',
          'name',
          'placeholder',
          'value',
          'defaultValue'
        )}
        className={textAreaClassName}
        {...actions}
        onChange={onChange}
        onPaste={onPaste}
        onDragStart={noDefault}
        draggable={false}
        ref={ref}
        $disabled={disabled}
        $hasError={isValid ? validationType === 'error' : hasError}
        $isSuccess={isValid ? validationType === 'success' : isCorrect}
        $leftIcon={leftIconPosition}
        $rightIcon={rightIconPosition}
        style={inputStyle}
        $borders={borders}
        $paddings={paddings}
        $color={color}
        $noBackground={noBackground}
        $textSize={textSize}
      />
      <TwTextAreaHeight
        className={textAreaClassName}
        style={inputStyle}
        $leftIcon={leftIconPosition}
        $rightIcon={rightIconPosition}
        $paddings={paddings}
        $textSize={textSize}
      >
        {value || props.value || props.defaultValue || ' '}
      </TwTextAreaHeight>
    </div>
  )
}
